/**
 * firebase.ts — D.1.2.2 Firebase App Initialization
 *
 * This module initializes the Firebase Web SDK once and exports the singleton
 * `auth` instance used by the rest of the application.
 *
 * Security notes:
 *   - Firebase config values are intentionally PUBLIC (they are not secrets).
 *     Security is enforced via Firebase Security Rules and Auth rules on the
 *     server, NOT by keeping these values private.
 *   - The actual API key et al. come from VITE_* env variables so they can
 *     vary per environment (local / staging / production).
 *
 * Usage:
 *   import { auth } from '@/services/firebase';
 *   // Use auth with Firebase Auth functions
 */
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';

// ---------------------------------------------------------------------------
// Firebase configuration
// ---------------------------------------------------------------------------

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// ---------------------------------------------------------------------------
// Singleton initialization
// ---------------------------------------------------------------------------

/**
 * Initialize the Firebase app only once.
 * In development with hot-module replacement (HMR), this module may be
 * re-evaluated. The `getApps()` check prevents duplicate app errors.
 */
const app: FirebaseApp =
  getApps().length === 0 ? initializeApp(firebaseConfig) : (getApps()[0] as FirebaseApp);

/**
 * Firebase Authentication instance.
 *
 * This is the single entry point for all auth operations. Do not call
 * `getAuth()` anywhere else in the app — import this instead.
 */
const auth: Auth = getAuth(app);

export { app, auth };
export type { FirebaseApp, Auth };
